/**
 * Offline Storage Utility using IndexedDB
 * Handles local caching of products and queuing of offline sales
 */

const DB_NAME = 'pos_offline_db';
const DB_VERSION = 1;

// Store names
const STORES = {
  PRODUCTS: 'products',
  OFFLINE_SALES: 'offline_sales',
  SETTINGS: 'settings',
  CUSTOMERS: 'customers',
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Products store - for offline product lookup
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productStore = database.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
        productStore.createIndex('plu_code', 'plu_code', { unique: false });
        productStore.createIndex('barcode', 'barcode', { unique: false });
        productStore.createIndex('name', 'name', { unique: false });
      }

      // Offline sales queue - for sales made while offline
      if (!database.objectStoreNames.contains(STORES.OFFLINE_SALES)) {
        const salesStore = database.createObjectStore(STORES.OFFLINE_SALES, { keyPath: 'offline_id' });
        salesStore.createIndex('created_at', 'created_at', { unique: false });
        salesStore.createIndex('synced', 'synced', { unique: false });
      }

      // Settings store - for offline settings
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }

      // Customers store - for offline customer lookup
      if (!database.objectStoreNames.contains(STORES.CUSTOMERS)) {
        const customerStore = database.createObjectStore(STORES.CUSTOMERS, { keyPath: 'id' });
        customerStore.createIndex('name', 'name', { unique: false });
        customerStore.createIndex('phone', 'phone', { unique: false });
      }

      console.log('IndexedDB stores created');
    };
  });
};

/**
 * Get a transaction and object store
 */
const getStore = (storeName, mode = 'readonly') => {
  if (!db) {
    throw new Error('Database not initialized. Call initOfflineDB() first.');
  }
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

/**
 * Cache all products for offline use
 */
export const cacheProducts = async (products) => {
  await initOfflineDB();
  const store = getStore(STORES.PRODUCTS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    let completed = 0;
    const total = products.length;

    if (total === 0) {
      resolve(0);
      return;
    }

    // Clear existing products first
    const clearRequest = store.clear();
    clearRequest.onsuccess = () => {
      // Add all products
      products.forEach((product) => {
        const request = store.add(product);
        request.onsuccess = () => {
          completed++;
          if (completed === total) {
            console.log(`Cached ${total} products for offline use`);
            resolve(total);
          }
        };
        request.onerror = () => {
          console.error('Error caching product:', product.id);
        };
      });
    };
    clearRequest.onerror = () => reject(clearRequest.error);
  });
};

/**
 * Get all cached products
 */
export const getCachedProducts = async () => {
  try {
    await initOfflineDB();
    const store = getStore(STORES.PRODUCTS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => {
        console.error('Error getting cached products:', request.error);
        resolve([]); // Return empty array instead of rejecting
      };
    });
  } catch (error) {
    console.error('Error accessing IndexedDB:', error);
    return [];
  }
};

/**
 * Search cached products by PLU, barcode, or name
 */
export const searchCachedProducts = async (query) => {
  try {
    const products = await getCachedProducts();
    if (!products || products.length === 0) {
      return [];
    }
    const searchLower = query.toLowerCase();
    
    return products.filter(p => 
      p.plu_code?.toLowerCase().includes(searchLower) ||
      p.barcode?.toLowerCase().includes(searchLower) ||
      p.name?.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower)
    ).slice(0, 50);
  } catch (error) {
    console.error('Error searching cached products:', error);
    return [];
  }
};

/**
 * Get a cached product by PLU code or barcode
 */
export const getCachedProductByCode = async (code) => {
  try {
    const products = await getCachedProducts();
    if (!products || products.length === 0) {
      return null;
    }
    return products.find(p => 
      p.plu_code === code || 
      p.barcode === code ||
      p.plu_code === code.replace(/^0+/, '') ||
      p.barcode === code.replace(/^0+/, '')
    );
  } catch (error) {
    console.error('Error getting product by code:', error);
    return null;
  }
};

/**
 * Queue a sale for later sync
 */
export const queueOfflineSale = async (saleData) => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES, 'readwrite');
  
  const offlineSale = {
    ...saleData,
    offline_id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    created_at: new Date().toISOString(),
    synced: false,
    sync_attempts: 0,
    is_offline_sale: true,
  };

  return new Promise((resolve, reject) => {
    const request = store.add(offlineSale);
    request.onsuccess = () => {
      console.log('Sale queued for offline sync:', offlineSale.offline_id);
      resolve(offlineSale);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all pending (unsynced) offline sales
 */
export const getPendingOfflineSales = async () => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const pending = (request.result || []).filter(sale => !sale.synced);
      resolve(pending);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get count of pending offline sales
 */
export const getPendingOfflineSalesCount = async () => {
  const pending = await getPendingOfflineSales();
  return pending.length;
};

/**
 * Mark a sale as synced
 */
export const markSaleAsSynced = async (offlineId, serverId) => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const getRequest = store.get(offlineId);
    getRequest.onsuccess = () => {
      const sale = getRequest.result;
      if (sale) {
        sale.synced = true;
        sale.server_id = serverId;
        sale.synced_at = new Date().toISOString();
        const updateRequest = store.put(sale);
        updateRequest.onsuccess = () => resolve(sale);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Sale not found'));
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Update sync attempt count for failed syncs
 */
export const updateSyncAttempt = async (offlineId, error) => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const getRequest = store.get(offlineId);
    getRequest.onsuccess = () => {
      const sale = getRequest.result;
      if (sale) {
        sale.sync_attempts = (sale.sync_attempts || 0) + 1;
        sale.last_sync_error = error;
        sale.last_sync_attempt = new Date().toISOString();
        const updateRequest = store.put(sale);
        updateRequest.onsuccess = () => resolve(sale);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Sale not found'));
      }
    };
    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Delete a synced sale from local storage
 */
export const deleteSyncedSale = async (offlineId) => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.delete(offlineId);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all synced sales (cleanup)
 */
export const clearSyncedSales = async () => {
  await initOfflineDB();
  const store = getStore(STORES.OFFLINE_SALES, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => {
      const sales = request.result || [];
      let deleted = 0;
      const synced = sales.filter(s => s.synced);
      
      if (synced.length === 0) {
        resolve(0);
        return;
      }

      synced.forEach(sale => {
        const deleteRequest = store.delete(sale.offline_id);
        deleteRequest.onsuccess = () => {
          deleted++;
          if (deleted === synced.length) {
            resolve(deleted);
          }
        };
      });
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Cache customers for offline use
 */
export const cacheCustomers = async (customers) => {
  await initOfflineDB();
  const store = getStore(STORES.CUSTOMERS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const clearRequest = store.clear();
    clearRequest.onsuccess = () => {
      let completed = 0;
      if (customers.length === 0) {
        resolve(0);
        return;
      }
      
      customers.forEach((customer) => {
        const request = store.add(customer);
        request.onsuccess = () => {
          completed++;
          if (completed === customers.length) {
            resolve(customers.length);
          }
        };
        request.onerror = () => completed++;
      });
    };
    clearRequest.onerror = () => reject(clearRequest.error);
  });
};

/**
 * Get cached customers
 */
export const getCachedCustomers = async () => {
  await initOfflineDB();
  const store = getStore(STORES.CUSTOMERS);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Save a setting to offline storage
 */
export const saveSetting = async (key, value) => {
  await initOfflineDB();
  const store = getStore(STORES.SETTINGS, 'readwrite');
  
  return new Promise((resolve, reject) => {
    const request = store.put({ key, value, updated_at: new Date().toISOString() });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a setting from offline storage
 */
export const getSetting = async (key, defaultValue = null) => {
  await initOfflineDB();
  const store = getStore(STORES.SETTINGS);
  
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.value ?? defaultValue);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get last sync timestamp
 */
export const getLastSyncTime = async () => {
  return getSetting('last_sync_time', null);
};

/**
 * Set last sync timestamp
 */
export const setLastSyncTime = async () => {
  return saveSetting('last_sync_time', new Date().toISOString());
};

/**
 * Check if we have cached products
 */
export const hasCachedProducts = async () => {
  const products = await getCachedProducts();
  return products.length > 0;
};

/**
 * Get offline database stats
 */
export const getOfflineStats = async () => {
  const products = await getCachedProducts();
  const pendingSales = await getPendingOfflineSales();
  const lastSync = await getLastSyncTime();
  
  return {
    cachedProducts: products.length,
    pendingSales: pendingSales.length,
    lastSyncTime: lastSync,
    isReady: products.length > 0,
  };
};

export default {
  initOfflineDB,
  cacheProducts,
  getCachedProducts,
  searchCachedProducts,
  getCachedProductByCode,
  queueOfflineSale,
  getPendingOfflineSales,
  getPendingOfflineSalesCount,
  markSaleAsSynced,
  updateSyncAttempt,
  deleteSyncedSale,
  clearSyncedSales,
  cacheCustomers,
  getCachedCustomers,
  saveSetting,
  getSetting,
  getLastSyncTime,
  setLastSyncTime,
  hasCachedProducts,
  getOfflineStats,
};
